/**
 * Phase 40 — one tolerant reader of a pool for the DTO, the loaders, the
 * fill and the editor. A LEGACY pool (no row carries `senders`) is converted
 * on read: Phase 27's `staticSenders` on senderDefaultsJson go to the lanes
 * of the gateways they name (null routeIds = every lane), the AI group is
 * the container's default. The stored JSON takes the lane shape on the
 * first save (`lanesToPoolJson`); nothing is backfilled at boot.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_SENDERS_PER_DROP_MAX, parseStaticSenders, senderIdProblem, staticSendersFor } from "../senders/static-senders";
import { senderGroupIds } from "./sender-group-ids";
import { DROP_GEN_LANE_ID, type DropGenGatewayLane, type DropGenLaneSenders } from "./gateway-lane.types";

/** The deterministic id of a legacy row: `r<routeId>`, `r<routeId>-2` for an accidental second row of the same gateway. */
export const legacyLaneId = (routeId: number, n = 0): string => (n === 0 ? `r${routeId}` : `r${routeId}-${n + 1}`);

export const isLaneId = (v: unknown): v is string => typeof v === "string" && DROP_GEN_LANE_ID.test(v);

type RawRow = Record<string, unknown> & { routeId: number };

const rawRows = (poolJson: unknown): RawRow[] =>
  Array.isArray(poolJson)
    ? poolJson.filter((r): r is RawRow => typeof r === "object" && r !== null && typeof (r as RawRow).routeId === "number" && Number.isInteger((r as RawRow).routeId) && (r as RawRow).routeId > 0)
    : [];

const hasSenders = (r: RawRow): boolean => typeof r.senders === "object" && r.senders !== null;

/** Sender IDs as the fill and the editor read them: trimmed, valid, no duplicates, within SMSWiser's cap. */
export function parseLaneSenderIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    const name = typeof v === "string" ? v.trim() : "";
    if (senderIdProblem(name) !== null || out.some((s) => s.toUpperCase() === name.toUpperCase())) continue;
    out.push(name);
    if (out.length === DROP_GEN_SENDERS_PER_DROP_MAX) break;
  }
  return out;
}

const laneSenders = (r: RawRow): DropGenLaneSenders => {
  const s = (r.senders ?? {}) as Record<string, unknown>;
  return {
    ids: parseLaneSenderIds(s.ids),
    ...(s.aiGroupIds !== undefined ? { aiGroupIds: s.aiGroupIds === null ? null : senderGroupIds(s.aiGroupIds) } : {}),
    ...(s.staticOnly !== undefined ? { staticOnly: s.staticOnly === true } : {}),
    ...(typeof s.amount === "number" && Number.isInteger(s.amount) && s.amount >= 1 && s.amount <= 15 ? { amount: s.amount } : {}),
    ...(s.mode === "SHARED" || s.mode === "UNIQUE" ? { mode: s.mode } : {}),
    ...(s.aiMode === "FALLBACK" || s.aiMode === "ALWAYS" ? { aiMode: s.aiMode } : {}),
    aiGroupId: typeof s.aiGroupId === "string" && s.aiGroupId.length > 0 ? s.aiGroupId : null,
  };
};

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const cap = (v: unknown): number | null => (typeof v === "number" && v >= 1 ? Math.floor(v) : null);

/** The pool's lanes in order. Legacy rows (no `senders` anywhere) take Phase 27's static senders. */
export function parseLanes(poolJson: unknown, senderDefaultsJson?: unknown): DropGenGatewayLane[] {
  const rows = rawRows(poolJson);
  const legacy = rows.every((r) => !hasSenders(r));
  const defaults = typeof senderDefaultsJson === "object" && senderDefaultsJson !== null ? (senderDefaultsJson as Record<string, unknown>) : {};
  const fixed = legacy ? parseStaticSenders(defaults.staticSenders) : [];
  const perRoute = new Map<number, number>();
  const taken = new Set<string>();
  return rows.map((r) => {
    const n = perRoute.get(r.routeId) ?? 0;
    perRoute.set(r.routeId, n + 1);
    let id = isLaneId(r.id) && !taken.has(r.id) ? r.id : legacyLaneId(r.routeId, n);
    for (let k = 2; taken.has(id); k += 1) id = `${legacyLaneId(r.routeId, n)}-${k}`;
    taken.add(id);
    const senders = legacy ? { ids: staticSendersFor(fixed, r.routeId), aiGroupId: null } : laneSenders(r);
    return { id, routeId: r.routeId, weight: num(r.weight), capacity: cap(r.capacity), senders };
  });
}

/** The pool as it is stored from now on — every row in the lane shape, ids kept. */
export const lanesToPoolJson = (lanes: readonly DropGenGatewayLane[]): Array<Record<string, unknown>> =>
  lanes.map((l) => ({ id: l.id, routeId: l.routeId, weight: l.weight, capacity: l.capacity, senders: { ...l.senders, ids: [...l.senders.ids] } }));

/** senderDefaultsJson without the retired `staticSenders` (they live on the lanes now). */
export function stripStaticSenders(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== "object" || raw === null) return null;
  const { staticSenders: _retired, ...rest } = raw as Record<string, unknown>;
  void _retired;
  return rest;
}
